/**
 * Task primitive — F 72 MVP phase 1 + F 83 Stage 2 (5-status model)
 *
 * Tasks are first-class entities in the FreshCards node substrate. They can
 * be linked from inline checkbox blocks in pages, captured via Cmd+T,
 * surfaced in the Today view, and projected across the universal entity
 * table.
 *
 * Design refs:
 *   - F 68 §2 — tasks-live-where-content-lives (Task as primitive)
 *   - F 70 Q1 — additive roll-up (parent shows progress; manual close by default)
 *   - F 70 Q3 — chrono-node + rrule (dual-pass NL parser; v1.1 wiring)
 *   - F 70 Q4 — horizontal timeline
 *   - F 70 Q5 — pragmatic 5-kind whitelist (pages, tasks, databases, artifacts, chat messages)
 *   - F 83 Stage 2 — 5-status model: inbox | today | upcoming | anytime | done
 *
 * v1 scope:
 *   - IndexedDB-backed CRUD via TaskStore (Phase 1d)
 *   - Filesystem-mirrored entities via filesystem-adapter (kind: task classification)
 *   - assignedTo inert (single-user v1 per F 68 Q4 / F 69 decision)
 *   - Soft-delete pattern deferred (Phase F 76 / F 78)
 */

import type { BaseEntity } from './BaseEntity';

/**
 * F 83 Stage 2: 5-status model.
 *   - 'inbox'    — unprocessed, default landing for Cmd+T capture
 *   - 'today'    — committed to today (user-set or due=today)
 *   - 'upcoming' — scheduled / due in future
 *   - 'anytime'  — no date, not done (someday/maybe)
 *   - 'done'     — completed (with restore)
 *
 * Backward compat: 'open' and 'cancelled' are accepted on read; on save they
 * are normalized to 'inbox' (open → inbox per dispatch migration rule) or
 * retained as 'cancelled'.
 */
export type TaskStatus = 'inbox' | 'today' | 'upcoming' | 'anytime' | 'done' | 'open' | 'cancelled';

/** The 5-status surface — what UI surfaces + filesystem-adapter speak. */
export type TaskViewStatus = 'inbox' | 'today' | 'upcoming' | 'anytime' | 'done';

/** 1 = highest priority (do first); 4 = lowest. Default 3. */
export type TaskPriority = 1 | 2 | 3 | 4;

export interface Task extends BaseEntity {
  /** ULID-shaped string. v1: crypto.randomUUID as ULID proxy. */
  id: string;
  title: string;
  status: TaskStatus;
  /** ISO date (YYYY-MM-DD) — null means no due date. F 83 Stage 2: the dispatch's "dueDate". */
  due: string | null;
  /** F 83 Stage 2: scheduled date (when to work on it). null means anytime / not scheduled. */
  scheduledDate: string | null;
  /** ISO date (YYYY-MM-DD) — null means no defer date. */
  defer: string | null;
  priority: TaskPriority;
  tags: string[];
  /** Optional list/section grouping (e.g. "inbox", "today", "someday"). */
  listId: string | null;
  /** Parent task id for hierarchy (F 70 Q1 — additive roll-up). */
  parentId: string | null;
  /** RRULE string (e.g. "FREQ=WEEKLY;BYDAY=MO") — null if non-recurring. */
  recurrenceRule: string | null;
  /** v1: inert. Schema reserved for v1.1 multi-user. */
  assignedTo: string | null;
  /** ISO 8601 datetime string. */
  createdAt: string;
  /** ISO 8601 datetime string. */
  updatedAt: string;
  /** ISO 8601 datetime string — null if not done. */
  completedAt: string | null;
  /** Page id this task is linked from (backlink). null for inbox-only tasks. */
  pageId: string | null;
  /** F 83 Stage 2: free-form notes. */
  notes: string;
  /** F 83 Stage 2: explicit user ordering within a view. Default 0. Persisted by drag-reorder. */
  order: number;
  /** Canonical fcid per F 45 filesystem-adapter scope. */
  fcid: string;
}

export const TASK_STATUSES: readonly TaskStatus[] = [
  'inbox',
  'today',
  'upcoming',
  'anytime',
  'done',
  'open',
  'cancelled',
] as const;

/** The 5-status set used by sidebar entries and processing actions. */
export const TASK_VIEW_STATUSES: readonly TaskViewStatus[] = [
  'inbox',
  'today',
  'upcoming',
  'anytime',
  'done',
] as const;

export const TASK_PRIORITIES: readonly TaskPriority[] = [1, 2, 3, 4] as const;

/** Default list when a task is captured via Cmd+T (F 70 Q2 / F 68 Q1 — tasks live where content lives). */
export const TASK_INBOX_LIST = 'inbox';

/** Task frontmatter trigger keys (used by entityFromFrontmatter to detect tasks on disk). */
export const TASK_FRONTMATTER_KEYS = {
  TASK_FLAG: 'task',
  STATUS: 'status',
  DUE: 'due',
  PRIORITY: 'priority',
  TAGS: 'tags',
} as const;

export function createTask(input: {
  title: string;
  due?: string | null;
  scheduledDate?: string | null;
  defer?: string | null;
  priority?: TaskPriority;
  tags?: string[];
  listId?: string | null;
  parentId?: string | null;
  recurrenceRule?: string | null;
  assignedTo?: string | null;
  pageId?: string | null;
  notes?: string;
  order?: number;
  status?: TaskStatus;
  /** Optional id override (for tests + filesystem-import). Defaults to generateTaskId(). */
  id?: string;
  /** Optional createdAt override (for tests + filesystem-import). */
  createdAt?: string;
}): Task {
  const now = new Date().toISOString();
  const id = input.id ?? generateTaskId();
  return {
    id,
    title: input.title,
    status: input.status ?? 'inbox',
    due: input.due ?? null,
    scheduledDate: input.scheduledDate ?? null,
    defer: input.defer ?? null,
    priority: input.priority ?? 3,
    tags: input.tags ?? [],
    listId: input.listId ?? null,
    parentId: input.parentId ?? null,
    recurrenceRule: input.recurrenceRule ?? null,
    assignedTo: input.assignedTo ?? null,
    createdAt: input.createdAt ?? now,
    updatedAt: now,
    completedAt: null,
    pageId: input.pageId ?? null,
    notes: input.notes ?? '',
    order: input.order ?? 0,
    fcid: `fcid://task/${id}`,
  };
}

/**
 * F 83 Stage 2: Normalize legacy or in-flight statuses to a view status.
 *   - 'open'      → 'inbox'   (per dispatch migration rule: open → inbox)
 *   - 'cancelled' → 'done'    (treat cancelled as terminal alongside done)
 *   - anything else: returned as-is if it's a valid TaskStatus
 */
export function normalizeTaskStatus(s: string | undefined | null): TaskViewStatus {
  if (s === 'open') return 'inbox';
  if (s === 'cancelled') return 'done';
  if (TASK_VIEW_STATUSES.includes(s as TaskViewStatus)) return s as TaskViewStatus;
  return 'inbox';
}

/**
 * Generate a Task id. v1 uses crypto.randomUUID() as a ULID proxy.
 * Production v1.1 will swap to ulid npm package (sortable by creation time).
 * The 1-line swap is in this function only — callers don't change.
 */
export function generateTaskId(): string {
  // crypto.randomUUID returns 36-char hex with dashes; format matches ULID
  // lex-sort for our purposes. Real ULID migration is a 1-line swap when
  // npm-installed.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Node/test fallback (jsdom has crypto.randomUUID but older runtimes may not)
  return `task-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Type-guard: is the value a valid Task? */
export function isTask(value: unknown): value is Task {
  if (!value || typeof value !== 'object') return false;
  const t = value as Record<string, unknown>;
  return (
    typeof t.id === 'string' &&
    typeof t.title === 'string' &&
    typeof t.status === 'string' &&
    (TASK_STATUSES as readonly string[]).includes(t.status as string) &&
    typeof t.fcid === 'string' &&
    t.fcid.startsWith('fcid://task/')
  );
}

/** Sentinel: is the task overdue? Today view + Inbox use this. */
export function isOverdue(task: Task, now: Date = new Date()): boolean {
  if (!task.due) return false;
  if (task.status === 'done' || task.status === 'cancelled') return false;
  return task.due < now.toISOString().slice(0, 10);
}

/** Sentinel: is the task due today? Today view shows these. */
export function isDueToday(task: Task, now: Date = new Date()): boolean {
  if (!task.due) return false;
  if (task.status === 'done' || task.status === 'cancelled') return false;
  return task.due === now.toISOString().slice(0, 10);
}

/** Mark a task as done. Returns updated task. */
export function markDone(task: Task): Task {
  if (task.status === 'done') return task;
  return {
    ...task,
    status: 'done',
    completedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/** Re-open a task. Returns updated task. */
export function reopenTask(task: Task): Task {
  if (task.status !== 'done' && task.status !== 'cancelled') return task;
  return {
    ...task,
    status: 'open',
    completedAt: null,
    updatedAt: new Date().toISOString(),
  };
}

/** Compact icon-glyph for a status. Used inline in lists and badges. */
export function statusLabel(s: TaskStatus): string {
  switch (s) {
    case 'inbox':
      return '📥';
    case 'open':
      return '○';
    case 'done':
      return '✓';
    case 'cancelled':
      return '⊘';
    default:
      return s;
  }
}
